import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  AbstractControl,
  FormControl,
} from '@angular/forms';
import { IconComponent } from '@ui/icon/icon.component';
import { InputComponent } from '@ui/input/input.component';
import { SelectComponent } from '@ui/select/select.component';

@Component({
  selector: 'app-request-details-step',
  standalone: true,
  imports: [
    CommonModule,
    InputComponent,
    ReactiveFormsModule,
    IconComponent,
    SelectComponent,
  ],
  template: `
    <div class="space-y-6">
      <!-- WMO gegevens section -->
      <div class="space-y-4">
        @if(requestForm.get('type')?.value === 'request') {
          <div>
            <ui-input
              label="SO referentie"
              [formControl]="soReferenceControl"
              [required]="false"
              placeholder="Service en Onderhoud referentie"
              [disabled]="false"
            ></ui-input>
          </div>
          <div>
            <ui-input
              label="Factuur referentie"
              [formControl]="factuurreferentieControl"
              [required]="false"
              placeholder="Optionele referentie voor uw administratie"
              [disabled]="false"
            ></ui-input>
          </div>
        <div class="grid grid-cols-1 gap-4">
          <ui-input
            label="Beschrijving aanvraag"
            name="description"
            [formControl]="descriptionControl"
            type="textarea"
            placeholder="Vul hier informatie in die relevant is voor de inmeting. Bijv. gebruikersgewicht, medische beperkingen, informatie over medebewoners"
          ></ui-input>
        </div>
        }
      </div>

      <!-- Cliënt gegevens section -->
      <div class="space-y-4">
        <h2 class="text-lg font-medium flex items-center gap-2">
          <ui-icon name="user-circle" class="w-5 h-5"></ui-icon>
          Cliënt gegevens
        </h2>
        <div class="grid grid-cols-2 gap-4">
          <ui-input
            label="Voornaam"
            name="first_name"
            [formControl]="firstNameControl"
            [required]="true"
            placeholder="Voornaam"
          ></ui-input>
          <ui-input
            label="Achternaam"
            name="last_name"
            [formControl]="lastNameControl"
            [required]="true"
            placeholder="Achternaam"
          ></ui-input>
          <ui-input
            label="Geboortedatum"
            name="date_of_birth"
            [formControl]="dateOfBirthControl"
            [required]="true"
            placeholder="DD-MM-YYYY"
            type="date"
            [max]="maxDate"
            [error]="getDateOfBirthErrorMessage()"
          ></ui-input>
          <ui-input
            label="BSN nummer"
            name="bsn"
            [formControl]="bsnControl"
            [required]="false"
            placeholder="Bijv. 123456789"
            [error]="getBsnErrorMessage()"
          ></ui-input>
          <ui-input
            label="Telefoonnummer"
            name="phone"
            [formControl]="phoneControl"
            [required]="true"
            placeholder="Telefoonnummer"
            [error]="getPhoneErrorMessage()"
          ></ui-input>
          <ui-input
            label="Email"
            name="email"
            type="email"
            [formControl]="emailControl"
            placeholder="Email"
          ></ui-input>
        </div>
      </div>

      <!-- Locatie gegevens section -->
      <div class="space-y-4">
        <h2 class="text-lg font-medium flex items-center gap-2">
          <ui-icon name="map-pin" class="w-5 h-5"></ui-icon>
          Locatie gegevens
        </h2>
        <div class="grid grid-cols-2 gap-4">
          <ui-input
            label="Straat"
            name="street"
            [formControl]="streetControl"
            [required]="true"
          ></ui-input>
          <div class="grid grid-cols-2 gap-4 flex-grow w-full">
            <ui-input
              label="Huisnummer"
              name="housenumber"
              [formControl]="housenumberControl"
              [required]="true"
            ></ui-input>
            <ui-input
              label="Huisnummer toevoeging"
              name="housenumber_addon"
              [formControl]="housenumberAddonControl"
              [required]="false"
            ></ui-input>
          </div>
          <ui-input
            label="Postcode"
            name="postal_code"
            [formControl]="postalCodeControl"
            [required]="true"
          ></ui-input>
          <ui-input
            label="Plaats"
            name="city"
            [formControl]="cityControl"
            [required]="true"
          ></ui-input>
        </div>
      </div>

      <!-- Opmerkingen section (alleen bij terugname aanvraag) -->
      @if(requestForm.get('type')?.value === 'withdrawal') {
      <div class="space-y-4">
        <h2 class="text-lg font-medium flex items-center gap-2">
          <ui-icon name="chat-bubble-left-right" class="w-5 h-5"></ui-icon>
          Opmerkingen
        </h2>
        <div class="grid grid-cols-1 gap-4">
          <ui-input
            label=""
            name="remarks"
            [formControl]="remarksControl"
            type="textarea"
            placeholder="Optionele opmerkingen voor de terugname aanvraag"
          ></ui-input>
        </div>
      </div>
      }
    </div>
  `,
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestDetailsStepComponent {
  private fb = inject(FormBuilder);

  @Input() requestForm!: FormGroup;
  @Input() isReadOnly: boolean = false;

      ngOnInit() {
    // Programmatisch uitschakelen van form controls als isReadOnly true is
    if (this.isReadOnly) {
      this.disableNonReferenceFields();
    }
  }

  ngOnChanges() {
    // Reageer op veranderingen van isReadOnly input
    if (this.isReadOnly) {
      this.disableNonReferenceFields();
    } else {
      this.enableAllFields();
    }
  }

    private disableNonReferenceFields() {
    // Schakel alle velden uit behalve SO referentie en factuur referentie
    const fieldsToDisable = [
      'description', 'first_name', 'last_name', 'date_of_birth', 'bsn',
      'phone', 'email', 'street', 'housenumber', 'housenumber_addon',
      'postal_code', 'city', 'remarks'
    ];

    fieldsToDisable.forEach(fieldName => {
      const control = this.requestForm.get(fieldName);
      if (control) {
        control.disable();
      }
    });
  }

  private enableAllFields() {
    // Schakel alle velden in
    const fieldsToEnable = [
      'description', 'first_name', 'last_name', 'date_of_birth', 'bsn',
      'phone', 'email', 'street', 'housenumber', 'housenumber_addon',
      'postal_code', 'city', 'remarks'
    ];

    fieldsToEnable.forEach(fieldName => {
      const control = this.requestForm.get(fieldName);
      if (control) {
        control.enable();
      }
    });
  }

  // Add maxDate property
  maxDate = new Date().toISOString().split('T')[0];

  get descriptionControl(): FormControl {
    return this.requestForm.get('description') as FormControl;
  }

  get firstNameControl(): FormControl {
    return this.requestForm.get('first_name') as FormControl;
  }

  get lastNameControl(): FormControl {
    return this.requestForm.get('last_name') as FormControl;
  }

  get dateOfBirthControl(): FormControl {
    return this.requestForm.get('date_of_birth') as FormControl;
  }

  get bsnControl(): FormControl {
    return this.requestForm.get('bsn') as FormControl;
  }

  get phoneControl(): FormControl {
    return this.requestForm.get('phone') as FormControl;
  }

  get emailControl(): FormControl {
    return this.requestForm.get('email') as FormControl;
  }

  get streetControl(): FormControl {
    return this.requestForm.get('street') as FormControl;
  }

  get housenumberControl(): FormControl {
    return this.requestForm.get('housenumber') as FormControl;
  }

  get housenumberAddonControl(): FormControl {
    return this.requestForm.get('housenumber_addon') as FormControl;
  }

  get postalCodeControl(): FormControl {
    return this.requestForm.get('postal_code') as FormControl;
  }

  get cityControl(): FormControl {
    return this.requestForm.get('city') as FormControl;
  }

  get remarksControl(): FormControl {
    return this.requestForm.get('remarks') as FormControl;
  }

  get soReferenceControl(): FormControl {
    return this.requestForm.get('wmo_so_reference') as FormControl;
  }

  get factuurreferentieControl(): FormControl {
    return this.requestForm.get('factuurreferentie') as FormControl;
  }

  getDateOfBirthErrorMessage(): string {
    const control = this.dateOfBirthControl;
    if (!control?.touched) return '';

    if (control.errors?.['required']) {
      return 'Geboortedatum is verplicht';
    }
    if (control.errors?.['futureDate']) {
      return 'Geboortedatum kan niet in de toekomst liggen';
    }
    return '';
  }

  getPhoneErrorMessage(): string {
    const control = this.phoneControl;
    if (!control?.touched) return '';

    if (control.errors?.['required']) {
      return 'Telefoonnummer is verplicht';
    }
    if (control.errors?.['invalidPhoneNumber']) {
      return 'Voer een geldig telefoonnummer in (bijv. 0612345678 of +31 6 12345678)';
    }
    return '';
  }

  getBsnErrorMessage(): string {
    const control = this.bsnControl;
    if (!control?.touched) return '';

    if (control.errors?.['required']) {
      return 'BSN nummer is verplicht';
    }
    if (control.errors?.['invalidBsn']) {
      return 'Voer een geldig BSN nummer in (9 cijfers)';
    }
    return '';
  }
}
